// Bloco "Acesso Meu Futuro App" da ficha do cliente (contrato C4 do backend-core).
// Módulo puro em CommonJS para rodar em `node tests/...` sem bundler (ver
// customer_chat/utils/messages.js).

const STATUS_ACTIVE = 'ACTIVE'
const STATUS_OVERDUE = 'OVERDUE'
const STATUS_CANCELED = 'CANCELED'
const STATUS_ENDED = 'ENDED'
const STATUS_REFUNDED = 'REFUNDED'
const STATUS_NONE = 'NONE'

const STATUS_MAP = {
  [STATUS_ACTIVE]: { label: 'Ativo', variant: 'success' },
  [STATUS_OVERDUE]: { label: 'Em atraso', variant: 'warning' },
  [STATUS_CANCELED]: { label: 'Cancelado', variant: 'secondary' },
  [STATUS_ENDED]: { label: 'Encerrado', variant: 'dark' },
  [STATUS_REFUNDED]: { label: 'Estornado', variant: 'danger' },
  [STATUS_NONE]: { label: 'Sem assinatura com direito', variant: 'light-secondary' },
}

const LINK_METHOD_LABELS = {
  PLANNER_CODE: 'Código do planejador',
  EMAIL_VERIFIED: 'E-mail verificado',
  LEAD_CREATED: 'Cadastro no app',
}

const LEGACY_LINK_LABEL = 'Vínculo antigo (sem prova)'

const GATEWAY_LABELS = {
  ASAAS: 'Asaas',
  PAGARME_V4: 'Pagar.me v4',
  PAGARME_V5: 'Pagar.me v5',
  PAGARME_V5_HUB: 'Pagar.me v5 (Hub)',
  NONE: 'Sem gateway',
  OTHER: 'Outro',
}

const BILLING_CYCLE_LABELS = {
  MONTHLY: 'Mensal',
  YEARLY: 'Anual',
}

const EMPTY = '—'

const pad = value => String(value).padStart(2, '0')

const isBlank = value => value === null || value === undefined || value === ''

// Status desconhecido (versão nova do backend) não pode quebrar a ficha: mostra o código cru.
const statusInfo = status => {
  if (isBlank(status)) return { ...STATUS_MAP[STATUS_NONE] }
  const known = STATUS_MAP[status]
  return known ? { ...known } : { label: String(status), variant: 'secondary' }
}

const isLinked = access => !!(access && !isBlank(access.meufuturoUserId))

// `null` só tem significado quando há vínculo: é o vínculo anterior à prova de posse.
const linkMethodLabel = access => {
  if (!isLinked(access)) return EMPTY
  const method = access.linkMethod
  if (isBlank(method)) return LEGACY_LINK_LABEL
  return LINK_METHOD_LABELS[method] || String(method)
}

/**
 * Data de calendário ("2026-11-10") → "10/11/2026". Lida como texto, sem `new Date`,
 * para não andar um dia para trás no fuso -03:00. Aceita prefixo de ISO completo.
 */
const formatDate = value => {
  if (isBlank(value)) return ''
  const match = /^(\d{4})-(\d{2})-(\d{2})/.exec(String(value))
  if (!match) return ''
  return `${match[3]}/${match[2]}/${match[1]}`
}

/** Instante ISO-8601 → "21/10/2026 15:02" no fuso do navegador. */
const formatDateTime = value => {
  if (isBlank(value)) return ''
  const date = new Date(value)
  if (Number.isNaN(date.getTime())) return ''
  return `${pad(date.getDate())}/${pad(date.getMonth() + 1)}/${date.getFullYear()} ${pad(date.getHours())}:${pad(date.getMinutes())}`
}

// Linha de apoio ao lado do badge.
const statusDetail = access => {
  if (!access) return ''
  if (access.status === STATUS_OVERDUE) {
    const grace = formatDate(access.graceUntil)
    if (grace) return `carência até ${grace}`
    const since = formatDate(access.overdueSince)
    return since ? `em atraso desde ${since}` : ''
  }
  if (access.status === STATUS_CANCELED) {
    const paid = formatDate(access.paidThrough)
    return paid ? `acesso até ${paid}` : ''
  }
  if (access.status === STATUS_ACTIVE) {
    const paid = formatDate(access.paidThrough)
    return paid ? `pago até ${paid}` : ''
  }
  return ''
}

const gatewayLabel = gateway => (isBlank(gateway) ? EMPTY : GATEWAY_LABELS[gateway] || String(gateway))

const billingCycleLabel = cycle => (isBlank(cycle) ? EMPTY : BILLING_CYCLE_LABELS[cycle] || String(cycle))

// `source` é null quando status = NONE.
const sourceSummary = access => {
  const source = access && access.source
  if (!source) return null
  return {
    contractId: isBlank(source.contractId) ? null : source.contractId,
    product: isBlank(source.financialSolutionId) ? EMPTY : `Solução #${source.financialSolutionId}`,
    contractStatus: isBlank(source.contractStatus) ? null : source.contractStatus,
    gateway: gatewayLabel(source.gateway),
    billingCycle: billingCycleLabel(source.billingCycle),
  }
}

const toVersion = value => {
  if (isBlank(value)) return null
  const number = Number(value)
  return Number.isFinite(number) ? number : null
}

/**
 * Situação da publicação do estado para o app:
 * - sem vínculo: nada é publicado (o evento só sai com meufuturoUserId);
 * - nunca publicado; publicação pendente (publishedVersion < version); em dia.
 */
const publicationState = access => {
  if (!access) return { key: 'unknown', label: EMPTY, variant: 'secondary' }
  if (!isLinked(access)) {
    return { key: 'unlinked', label: 'Não publicado (cliente sem vínculo com o app)', variant: 'secondary' }
  }
  const version = toVersion(access.version)
  const published = toVersion(access.publishedVersion)
  if (published === null || isBlank(access.lastPublishedAt)) {
    return { key: 'never', label: 'Nunca publicado', variant: 'warning' }
  }
  if (version !== null && published < version) {
    return { key: 'pending', label: `Publicação pendente (publicada v${published} de v${version})`, variant: 'warning' }
  }
  return { key: 'synced', label: 'Em dia', variant: 'success' }
}

// Erros do GET. 403/404 ficam no bloco — a instância HTTP do módulo não desloga.
const loadErrorMessage = error => {
  const status = error && error.response ? error.response.status : undefined
  if (status === 403) return 'Você não tem permissão para ver o acesso deste cliente ao Meu Futuro App.'
  if (status === 404) return 'Cliente não encontrado para consulta do acesso ao Meu Futuro App.'
  return 'Não foi possível carregar o acesso ao Meu Futuro App. Tente novamente.'
}

const republishErrorMessage = error => {
  const response = error && error.response
  const status = response ? response.status : undefined
  if (status === 403) return 'Você não tem permissão para republicar o estado deste cliente.'
  if (status === 404) return 'Cliente não encontrado para republicação.'
  const message = response && response.data && typeof response.data.message === 'string' ? response.data.message.trim() : ''
  return message || 'Não foi possível republicar o estado. Tente novamente.'
}

const republishSuccessMessage = data => {
  const version = toVersion(data && data.version)
  return version === null
    ? 'O estado foi recalculado e enfileirado para publicação.'
    : `O estado foi recalculado e enfileirado para publicação (versão ${version}).`
}

module.exports = {
  STATUS_ACTIVE,
  STATUS_OVERDUE,
  STATUS_CANCELED,
  STATUS_ENDED,
  STATUS_REFUNDED,
  STATUS_NONE,
  LEGACY_LINK_LABEL,
  EMPTY,
  statusInfo,
  isLinked,
  linkMethodLabel,
  formatDate,
  formatDateTime,
  statusDetail,
  gatewayLabel,
  billingCycleLabel,
  sourceSummary,
  publicationState,
  loadErrorMessage,
  republishErrorMessage,
  republishSuccessMessage,
}
